import React, { useRef, useLayoutEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, Center } from '@react-three/drei';
import * as THREE from 'three';

function ImportedTrophyModel({ scrollProgress }) {
  const trophyGroup = useRef();

  // Load the GLB file directly from the public folder
  const { scene } = useGLTF('/model.glb');

  // Enable realistic shadows, metallic sheen, and double-sided rendering on all imported meshes
  useLayoutEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        if (child.material) {
          child.material.side = THREE.DoubleSide;
        }
      }
    });
  }, [scene]);

  useFrame((state) => {
    if (!trophyGroup.current) return;

    const p = Math.min(Math.max(scrollProgress.get(), 0), 1);

    // ========================================================
    // 1. POP-UP ENTRANCE (0% -> 18% Scroll)
    // Adjust 1.2 to scale your 3D model up or down to fit
    // ========================================================
    const popFactor = THREE.MathUtils.smoothstep(p, 0.0, 0.18);
    trophyGroup.current.scale.setScalar(popFactor * 1.2);

    // ========================================================
    // 2. CONTINUOUS 360 ROTATION (15% -> 82% Scroll)
    // ========================================================
    const spinProgress = THREE.MathUtils.smoothstep(p, 0.15, 0.82);
    trophyGroup.current.rotation.y = spinProgress * Math.PI * 2;

    // ========================================================
    // 3. STAND UPRIGHT & SETTLE (65% -> 92% Scroll)
    // ========================================================
    const uprightProgress = THREE.MathUtils.smoothstep(p, 0.65, 0.92);
    const targetTiltX = THREE.MathUtils.lerp(-0.25, 0, uprightProgress);

    // Dynamic mouse parallax
    const mouseInfluence = 1 - uprightProgress * 0.7;
    const mouseX = ((state.pointer.x * Math.PI) / 36) * mouseInfluence;
    const mouseY = ((state.pointer.y * Math.PI) / 40) * mouseInfluence;

    trophyGroup.current.rotation.x = THREE.MathUtils.damp(
      trophyGroup.current.rotation.x,
      targetTiltX + mouseY,
      6,
      0.02
    );
    trophyGroup.current.rotation.z = THREE.MathUtils.damp(
      trophyGroup.current.rotation.z,
      mouseX,
      6,
      0.02
    );
  });

  return (
    <group ref={trophyGroup} position={[0, -0.05, 0]}>
      {/* <Center> guarantees that any imported model is perfectly balanced on its center pivot */}
      <Center>
        <primitive object={scene} />
      </Center>
    </group>
  );
}

export default function AppleStudioTrophy({ scrollProgress }) {
  return (
    <div style={{ width: '150%', height: '150%', position: 'relative' }}>
      <Canvas
        camera={{ position: [0, 0, 6.8], fov: 36 }}
        gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
        style={{ background: 'transparent' }}
      >
        {/* Studio lighting rig to bring out the metallic finish of your model */}
        <ambientLight intensity={2.2} />
        <directionalLight position={[5, 8, 7]} intensity={4.5} color="#fff8e7" />
        <directionalLight position={[-6, 4, -4]} intensity={2.8} color="#00f2fe" />
        <directionalLight position={[0, 6, -6]} intensity={3.0} color="#ffffff" />
        <pointLight position={[0, -3.5, 3]} intensity={3.5} color="#f59e0b" />
        <pointLight position={[0, 0.5, 4.5]} intensity={2.8} color="#fef08a" />

        <ImportedTrophyModel scrollProgress={scrollProgress} />
      </Canvas>
    </div>
  );
}

// Pre-load the GLB model in background so it renders instantly on mount
useGLTF.preload('/model.glb');